// =====================================================================
// PrerollFormModalExt.jsx — create / edit PREROLL AUDIO  (UTC ⇆ locale)
// =====================================================================
import React, { useState, useEffect } from 'react'
import Modal        from '../common/Modal'
import Loader       from '../Utils/Loader'
import api          from '../../services/api'
import { toBase64 } from '../Utils/file'
import { DateTime } from 'luxon'
import '../../css/prerollFormModal.css'

const API_BASE = '/prerolls-ext'

/* ───────── helpers ───────── */
const toInputDT = v => {
  if (!v) return ''
  const dt = v.includes('T')
    ? DateTime.fromISO(v, { zone: 'utc' })
    : DateTime.fromSQL(v, { zone: 'utc' })
  return dt.setZone('local').toFormat("yyyy-MM-dd'T'HH:mm")
}

const toUtcSql = s =>
  DateTime
    .fromFormat(s, "yyyy-MM-dd'T'HH:mm", { zone: 'local' })
    .toUTC()
    .toFormat('yyyy-MM-dd HH:mm:ss')

const EMPTY = {
  startTime:'', endTime:'', type:'base', prio:0,
  mediaBase64:'', originalFilename:'', coverBase64:'',
  mediaType:'audio'
}

/* ───────── component ───────── */
export default function PrerollFormModalExt({ open, onClose, editing, onSaved }) {
  const [data,      setData]    = useState(EMPTY)
  const [coverPrev, setCoverPrev] = useState(null)
  const [busy,      setBusy]    = useState({ media:false, cover:false })
  const [saving,    setSaving]  = useState(false)
  const isEdit = Boolean(editing)

  /* bootstrap / reset */
  useEffect(() => {
    if (isEdit && editing) {
      setData({
        ...EMPTY,
        ...editing,
        startTime: toInputDT(editing.startTime),
        endTime  : toInputDT(editing.endTime),
      })
      setCoverPrev(editing.coverUrl ?? null)
    } else { setData(EMPTY); setCoverPrev(null) }
  }, [isEdit, editing])

  /* handlers */
  const handleChange = e =>
    setData({ ...data, [e.target.name]: e.target.value })

  const pickFile = async (e, field, busyKey, previewCb = null) => {
    const file = e.target.files[0]; if (!file) return
    setBusy(b => ({ ...b, [busyKey]: true }))

    const b64 = await toBase64(file)
    setData(d => ({
      ...d,
      [field]: b64,
      ...(field === 'mediaBase64' ? { originalFilename: file.name } : {}),
    }))

    if (previewCb) previewCb(URL.createObjectURL(file))
    setBusy(b => ({ ...b, [busyKey]: false }))
  }

  const uploading = busy.media || busy.cover

  const handleSubmit = async e => {
    e.preventDefault()
    if (uploading || saving) return
    setSaving(true)
    try {
      const payload = {
        ...data,
        startTime: toUtcSql(data.startTime),
        endTime  : toUtcSql(data.endTime),
      }
      isEdit
        ? await api.put(`${API_BASE}/${editing.id}`, payload)
        : await api.post(API_BASE, payload)

      onSaved()
      onClose()
    } catch (err) {
      console.error(err)
      alert('Errore salvataggio preroll')
    } finally { setSaving(false) }
  }

  /* render */
  return (
    <Modal open={open} onClose={onClose}>
      {/* overlay loader (salvataggio) */}
      {(saving || uploading) && (
        <div className="modal-loader-overlay">
          <Loader />
        </div>
      )}

      <h3 className="preroll-modal-title">
        {isEdit ? 'Modifica preroll' : 'Nuovo preroll (audio)'}
      </h3>

      <form className="rr-form rr-modal-scroll" onSubmit={handleSubmit}>
        {/* date/time ------------------------------------------------ */}
        <label>Inizio
          <input type="datetime-local" name="startTime" value={data.startTime}
                 onChange={handleChange} required />
        </label>
        <label>Fine
          <input type="datetime-local" name="endTime" value={data.endTime}
                 onChange={handleChange} required />
        </label>

        {/* tipo + prio -------------------------------------------- */}
        <div className="rr-row">
          <label>Tipo
            <select name="type" value={data.type} onChange={handleChange}>
              <option value="base">Pubblicità</option>
              <option value="first">Primo</option>
              <option value="last">Ultimo</option>
            </select>
          </label>
          <label>Ordine
            <input type="number" name="prio" min="0" max="10"
                   value={data.prio} onChange={handleChange} />
          </label>
        </div>

        {/* media principale ---------------------------------------- */}
        <label>File audio (mp3)
          <input type="file" accept="audio/*"
                 onChange={e => pickFile(e, 'mediaBase64', 'media')}
                 required={!isEdit} />
        </label>
        {busy.media && <Loader small />}

        {/* cover opzionale ---------------------------------------- */}
        <label>Thumbnail / Cover (opz.)
          <input type="file" accept="image/*"
                 onChange={e => pickFile(e, 'coverBase64', 'cover', setCoverPrev)} />
          {busy.cover && <Loader small />}
          {coverPrev && !busy.cover && (
            <img src={coverPrev} alt="preview"
                 style={{ maxWidth:120, marginTop:6, borderRadius:4 }} />
          )}
        </label>

        {/* actions ------------------------------------------------- */}
        <div className="rr-actions">
          <button type="button" className="btn-cancel" onClick={onClose}>Annulla</button>
          <button type="submit" className="btn-save" disabled={uploading || saving}>
            {(uploading || saving) ? <Loader small /> : 'Salva'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
